import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// iCGChat note: the standard shadcn Badge ships with default / secondary /
// destructive / outline variants. We extend it with WhatsApp / Telegram / LINE
// channel tags so a conversation row can render the source channel inline.
// The channel colors come from tailwind.config.js (whatsapp/telegram/line),
// which are the official per-channel brand hexes.
const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        // Neumorphic: default (unread counters) and whatsapp (channel tag)
        // both wear the design's green accent gradient with a soft drop.
        default:
          'neu-gradient border-transparent text-white shadow-neu-flat',
        // Attention (unread count / marked-unread dot) — the v2 cyan
        // gradient with an inset edge ring. A dedicated variant rather than
        // `default` + className overrides: twMerge cannot strip the custom
        // `shadow-neu-flat` off `default` (unknown theme key), and the
        // compiled utilities order lets it beat an arbitrary shadow-[...].
        unread:
          'border-transparent bg-[linear-gradient(145deg,#2ec4d6,#1f9fb0)] text-white shadow-[2px_2px_5px_var(--neu-dark-mid),inset_0_0_0_2px_var(--edge)]',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        whatsapp:
          'neu-gradient border-transparent font-extrabold tracking-wider text-white shadow-neu-flat',
        telegram:
          'border-transparent bg-telegram text-telegram-foreground hover:bg-telegram/80',
        line: 'border-transparent bg-line text-line-foreground hover:bg-line/80',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps): React.JSX.Element {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
